import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { LIST_GATEWAYS_RESOURCE } from 'src/features/gateway/api/useListGateways';
import { useDeleteMembershipTypeZoneRule } from 'src/features/membershipType/api/zoneRule/useDeleteMembershipTypeZoneRule';
import { LIST_MEMBERSHIP_TYPE_ZONE_RULES_RESOURCE } from 'src/features/membershipType/api/zoneRule/useListMembershipTypeZoneRules';
import { zoneRulesMessages } from 'src/features/membershipType/utils/messages';
import { ZoneRule } from 'src/features/membershipType/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';
import { MEMBERSHIP_TYPE_RESOURCE } from 'src/features/membershipType/api/useGetMembershipType';
import { useOnError } from 'src/utils/errors';
import { MEMBERSHIP_TYPE_CONFIGURATION_RESOURCE } from 'src/features/membershipType/api/useGetMembershipTypeConfiguration';

interface DeleteZoneRuleProps<TData> extends DataTableNewEntryProps<TData> {
    selectedZoneRule: ZoneRule;
    children?: React.ReactNode;
}

function DeleteZoneRule<TData>({ selectedZoneRule, children }: DeleteZoneRuleProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage, locale } = useIntl();
    const { membershipTypeId = '' } = useParams();
    const { parkingId } = useParking();

    const onError = useOnError();

    const { mutate: deleteRule } = useDeleteMembershipTypeZoneRule({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_MEMBERSHIP_TYPE_ZONE_RULES_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [LIST_GATEWAYS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_TYPE_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_TYPE_CONFIGURATION_RESOURCE] });

            setIsModalOpen(false);
            toast({
                title: formatMessage(zoneRulesMessages.deleteZoneRuleSuccess),
                description: formatMessage(zoneRulesMessages.deleteZoneRuleSuccessDescription, {
                    entry: selectedZoneRule.zone.translations.find((translation) => translation.language === locale)
                        ?.name,
                }),
            });
        },
        onError: (error) => {
            onError(error, () => null, { inlineErrorStatuses: [] });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...zoneRulesMessages.deleteZoneRule },
                subTitle: {
                    ...zoneRulesMessages.deleteZoneRuleSubtitle,
                    values: {
                        entry: selectedZoneRule.zone.translations.find((temp) => temp.language === locale)?.name ?? '-',
                    },
                },
                icon: ICON_MAP.DELETE as IconProp,
                confirmationMessage: {
                    ...zoneRulesMessages.confirmDeleteZoneRule,
                    values: {
                        entry: selectedZoneRule.zone.translations.find((temp) => temp.language === locale)?.name ?? '-',
                    },
                },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: generalMessages.delete,
                callbackFn: () => deleteRule({ parkingId, membershipTypeId, ruleId: selectedZoneRule.id }),
            }}
        >
            {children}
        </ConfirmationModal>
    );
}

export default DeleteZoneRule;
